/**
 * VirtualList — the app-wide, generic windowed list primitive.
 *
 * One reusable component that owns a `@tanstack/react-virtual` virtualizer
 * internally and exposes a small, Virtuoso-like surface so callers never touch
 * `useVirtualizer` directly. It is the single virtualizer we standardise on
 * going forward — ChatV2's message list is the first consumer, but nothing in
 * here is chat-specific.
 *
 * **Mechanics only.** This component owns windowing, measurement and scroll
 * behaviour; it renders NO visual chrome of its own beyond the structural
 * boxes virtualization requires (a scroll container, a sized spacer, absolutely
 * positioned row wrappers). All markup, spacing and design tokens are supplied
 * by the consumer through `renderItem` and the slot props (`stickyHeader`,
 * `stickyFooter`, `overlay`, `emptyState`, `loadingState`). Style the scroll
 * container with `className`.
 *
 * **What the TanStack engine gives us (verified against virtual-core@3.17.6):**
 *   • `anchorTo: 'end'` — on any edge change (count or first/last key) it
 *     captures the item currently under the scroll offset and restores its
 *     viewport position after re-measure. That single mechanism makes BOTH
 *     prepending history jump-free (the visible anchor row stays put) AND
 *     streaming growth pin to the bottom.
 *   • `followOnAppend` — when a genuine append lands AND the user `isAtEnd`,
 *     scroll to the new end with the given behaviour; a scrolled-up reader is
 *     left alone.
 *   • Streaming growth of the last row is a *resize*, handled by the built-in
 *     ResizeObserver: while at the end it re-pins to the bottom, while scrolled
 *     up it does not drag the viewport. So a row that changes size AFTER it is
 *     rendered (streaming text, expanding cards, async image/code-block load)
 *     is measured correctly, not just on first render.
 *   • When content shrinks under the viewport (e.g. a long row is removed), the
 *     engine may have issued a compensation write the browser clamped, and it
 *     keeps that write PENDING — retried on every later resize until it lands.
 *     Our shrink clamp lands exactly on the max the engine recorded, which it
 *     reads as its own echo, so the stale target survives and drags the view
 *     down as new content grows. The shrink clamp therefore drops it
 *     (`dropPendingClampedAdjustment`).
 *
 * **Sizing.** Like every virtualizer it needs a real height: the scroll
 * container is `height: 100%`, so give it a sized parent (e.g. `flex-1
 * min-h-0`). Do NOT add vertical padding to the scroll container — it would
 * offset the item coordinate space (`scrollMargin` is 0). Use `paddingStart` /
 * `paddingEnd` / `gap` props for vertical spacing and `className` for
 * horizontal/visual styling.
 *
 * **Rows must not use margins.** ResizeObserver excludes margins from the
 * measured box, so row spacing must come from `gap`/padding — never a margin on
 * the measured wrapper.
 *
 * **Initial-mount settle (end-anchored lists).** A single extra frame is NOT
 * enough to catch every source of post-mount layout shift. Two independent
 * things resize rows after the very first paint, on their own schedules:
 *   1. Web font swap — the fallback font paints first; when the real font
 *      (e.g. Lato) finishes loading, EVERY row's height changes at once. This
 *      can land several frames after mount, well after a single `rAF` window
 *      would have already revealed the list.
 *   2. Any row with async-sized content (images, embeds, syntax highlighting)
 *      resizing once its real dimensions land.
 * `anchorTo: 'end'` correctly re-pins scroll position when either happens —
 * but if that correction happens AFTER the container is already visible, the
 * user sees the motion (settle at bottom, drift up as height changes
 * underneath, snap back down).
 *
 * The fix is to not guess a fixed number of frames: wait for
 * `document.fonts.ready` (bounded by a timeout so a slow/failed font load
 * can't hang the UI), then poll `getTotalSize()` across consecutive frames
 * and only reveal once it has stopped changing for two frames running — also
 * capped, so a row that keeps resizing indefinitely can't hide the list
 * forever. This trades a small bounded hidden window (typically 1-4 frames,
 * capped at ~200ms) for a landing that is visually exact instead of "close,
 * then corrected in front of the user."
 *
 * **The real permanent fix lives outside this component:** reserve layout
 * space for anything that resizes after mount — explicit width/height or
 * `aspect-ratio` on avatars/attachments/embeds inside row content, and
 * preload the webfont (`<link rel="preload" as="font">` or `font-display:
 * optional`) so it's already resident by the time this list mounts. Do that
 * and `document.fonts.ready` resolves instantly, the stabilization loop exits
 * on its first check, and there is no shift left to correct in the first
 * place. This component's job is to hide the seam when some shift is
 * unavoidable — not to be a substitute for preventing it.
 */

import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import {
  type CSSProperties,
  forwardRef,
  type ReactNode,
  type Ref,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore} from 'react';

type ScrollBehaviorOpt = 'auto' | 'smooth' | 'instant';

/** Minimal external store for a single boolean that changes outside React's
 * render cycle (driven by scroll events / the mount-settle sequence). `set`
 * only notifies subscribers when the value actually changes. */
function createBooleanStore(initial: boolean) {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => value,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    set(next: boolean) {
      if (next === value) {return;}
      value = next;
      listeners.forEach((l) => l());
    }
  };
}

/** Resolves once web fonts are loaded, or after `timeoutMs` — whichever is
 * first — so a slow or failed font load can never hang the settle sequence
 * indefinitely. */
function waitForFontsReady(timeoutMs: number): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts?.ready) {
    return Promise.resolve();
  }
  return Promise.race([
    document.fonts.ready.then(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))
  ]);
}

/** Discards the engine's pending clamped compensation write (virtual-core
 * `_clampedAdjustment`, no public reset). Called only when the content has
 * shrunk below the offset that write targets, so the target no longer exists.
 * A no-op if a future engine version renames the field. */
function dropPendingClampedAdjustment(
  virtualizer: Virtualizer<HTMLDivElement, HTMLDivElement>
): void {
  const engine = virtualizer as unknown as { _clampedAdjustment?: unknown };
  if ('_clampedAdjustment' in engine) {
    engine._clampedAdjustment = null;
  }
}

/** Repeatedly re-lands at the end and watches `getTotalSize()` across
 * animation frames, resolving once it has stopped changing for
 * `stableFramesNeeded` consecutive checks (or after `maxFrames`, as a safety
 * cap against content that never stops resizing). Returns a cancel function. */
function stabilizeAtEnd(
  virtualizer: Virtualizer<HTMLDivElement, HTMLDivElement>,
  opts: { maxFrames: number; stableFramesNeeded: number },
  onDone: () => void
): () => void {
  let cancelled = false;
  let rafId: number | undefined;
  let lastSize = -1;
  let stableFrames = 0;
  let frame = 0;

  const tick = () => {
    if (cancelled) {return;}
    virtualizer.scrollToEnd({ behavior: 'instant' });
    const size = virtualizer.getTotalSize();
    frame += 1;
    if (size === lastSize) {
      stableFrames += 1;
    } else {
      stableFrames = 0;
      lastSize = size;
    }
    if (stableFrames >= opts.stableFramesNeeded || frame >= opts.maxFrames) {
      onDone();
      return;
    }
    rafId = requestAnimationFrame(tick);
  };

  rafId = requestAnimationFrame(tick);
  return () => {
    cancelled = true;
    if (rafId != null) {cancelAnimationFrame(rafId);}
  };
}

/** Reactive snapshot handed to the `overlay` render slot so a consumer can
 * drive its own affordances (a "jump to latest" pill, an unseen-count badge)
 * without reaching into the virtualizer. */
export interface VirtualListController {
  /** True while the viewport sits within `scrollEndThreshold` of the end. */
  isAtEnd: boolean;
  /** Pixel distance from the bottom of the content to the viewport bottom. */
  distanceFromEnd: number;
  scrollToEnd: (opts?: { behavior?: ScrollBehaviorOpt }) => void;
  scrollToIndex: (
    index: number,
    opts?: {
      align?: 'start' | 'center' | 'end' | 'auto';
      behavior?: 'auto' | 'smooth';
    }
  ) => void;
}

/** Imperative handle exposed via `ref`. Callers drive scrolling and read the
 * at-end state without owning a virtualizer instance. `getVirtualizer` is the
 * escape hatch for the rare case a consumer needs the raw engine. */
export interface VirtualListHandle {
  scrollToIndex: (
    index: number,
    opts?: {
      align?: 'start' | 'center' | 'end' | 'auto';
      behavior?: 'auto' | 'smooth';
    }
  ) => void;
  scrollToEnd: (opts?: { behavior?: ScrollBehaviorOpt }) => void;
  isAtEnd: (threshold?: number) => boolean;
  measure: () => void;
  getVirtualizer: () => Virtualizer<HTMLDivElement, HTMLDivElement> | null;
}

/** A measured, in-flow row that sits OUTSIDE `data` — pinned before it
 * (`leadingRows`) or after it (`trailingRows`). Lets a consumer add affixes
 * (a load-older skeleton, a live streaming row) WITHOUT wrapping every `data`
 * item in a discriminated-union envelope: `data` stays the caller's source
 * array, passed by reference, so toggling an affix never re-allocates the N
 * item rows. Each affix is windowed, measured and keyed exactly like a data
 * row. */
export interface AffixRow {
  /** Stable key — must not collide with any `getItemKey(data)` result. */
  key: string;
  /** Row content; wrapped in the same measured box as every other row. */
  render: () => ReactNode;
}

export interface VirtualListProps<T> {
  /** The full, un-windowed list. Only the visible window is rendered. */
  data: readonly T[];
  /** Row renderer. Return the row's markup — the component wraps it in a
   * measured, absolutely-positioned box for you. */
  renderItem: (item: T, index: number) => ReactNode;
  /** Stable key per item — REQUIRED for jump-free prepend and for streaming
   * rows to update in place without remounting. */
  getItemKey: (item: T, index: number) => string | number;

  /** Measured rows pinned before `data` (e.g. a load-older skeleton). Prepend
   * stays jump-free under `anchorTo: 'end'`. Omit/empty = none. */
  leadingRows?: readonly AffixRow[];
  /** Measured rows pinned after `data` (e.g. the live streaming row). Appends
   * follow the end under `followOnAppend`. Omit/empty = none. */
  trailingRows?: readonly AffixRow[];

  /** Initial height guess per row (or a per-index fn). Real heights are
   * measured after render. Default 64. Tune this to your actual average row
   * height to shrink the correction the settle sequence has to absorb. */
  estimateSize?: number | ((index: number) => number);
  /** Rows rendered beyond the visible window on each side. Default 6. */
  overscan?: number;

  /** Which edge stays visually stable when data changes. `'end'` for
   * chat/reverse feeds (prepend-stable + bottom-growth); default `'start'`. */
  anchorTo?: 'start' | 'end';
  /** Follow the end when new items are appended AND the user is at the end.
   * `true` = 'auto'. Requires `anchorTo: 'end'`. Default off. */
  followOnAppend?: boolean | ScrollBehaviorOpt;
  /** Px proximity to the end that still counts as "at the end" (drives follow,
   * streaming-pin and `isAtEnd`). Default = engine default (1). */
  scrollEndThreshold?: number;
  /** When `anchorTo: 'end'`, land at the bottom the first time data appears.
   * Default true (only meaningful with end anchoring). */
  autoScrollToEndOnMount?: boolean;
  /** Max time (ms) to wait for `document.fonts.ready` before proceeding
   * anyway. Default 200. */
  fontReadyTimeoutMs?: number;
  /** Max animation frames the post-font stabilization loop will spend polling
   * for the total content height to stop changing, before giving up and
   * revealing anyway. Default 12 (~200ms at 60fps). */
  maxSettleFrames?: number;

  /** Vertical gap between rows (px). Preferred over row padding/margins. */
  gap?: number;
  paddingStart?: number;
  paddingEnd?: number;
  reserveEndSpaceForIndex?: number | null;

  /** Fires once when the viewport reaches near the top — e.g. load older
   * history. Re-arms after the viewport leaves the threshold band. */
  onStartReached?: () => void;
  /** Fires once when the viewport reaches near the bottom. */
  onEndReached?: () => void;
  /** Px band from the top that triggers `onStartReached`. Default 200. */
  startReachedThreshold?: number;
  /** Px band from the bottom that triggers `onEndReached`. Default 200. */
  endReachedThreshold?: number;
  /** Notified only when the at-end state flips (post-settle, authoritative). */
  onAtEndChange?: (atEnd: boolean) => void;
  /** When true, `onStartReached`/`onEndReached` only fire from genuine
   * scrolling — a resize/remeasure (e.g. a sibling row's measured height
   * changing) is ignored instead of being treated as a proximity change.
   * Default false, preserving existing behavior for callers that don't set
   * this. Opt in if rows in your list can resize/collapse independently of
   * `data` changing size, and a resize-triggered reach would be wrong. */
  gateReachedOnScroll?: boolean;
  /** Fires on every visible-range change (scroll or resize) with the TRUE
   * visible range — `overscan` padding excluded, unlike `getVirtualItems()` —
   * independent of `onStartReached`/`onEndReached`'s threshold/re-arm logic.
   * For consumers that need the current range (e.g. a sticky-header lookup)
   * without reaching into the virtualizer via `ref`. Omit if you don't need
   * this. */
  onRangeChange?: (startIndex: number, endIndex: number) => void;

  /** Styling for the scroll container (visual + horizontal only; no vertical
   * padding — see file header). */
  className?: string;
  /** Styling for the inner sized spacer. */
  listClassName?: string;
  /** Styling for each measured row wrapper (padding only — never margins). */
  itemClassName?: string;

  /** Overlaid at the top of the viewport, outside scroll flow (e.g. a sticky
   * date divider). Does not affect item measurement. */
  stickyHeader?: ReactNode;
  /** Overlaid at the bottom of the viewport, outside scroll flow. */
  stickyFooter?: ReactNode;
  /** An absolutely-positioned layer over the list — pass a node, or a function
   * of the live controller (e.g. a "jump to latest" pill). */
  overlay?: ReactNode | ((controller: VirtualListController) => ReactNode);

  /** Shown (in place of the list) while `isLoading` is true. */
  loadingState?: ReactNode;
  /** Shown (in place of the list) when `data` is empty and not loading. */
  emptyState?: ReactNode;
  isLoading?: boolean;

  'aria-label'?: string;
  role?: string;
}

/** Shared empty affix list — a stable reference so the default never triggers
 * a new-array-identity re-render or effect churn. */
const EMPTY_AFFIX_ROWS: readonly AffixRow[] = [];

const SCROLL_CONTAINER_BASE_STYLE: CSSProperties = {
  height: '100%',
  width: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  position: 'relative'
};

function VirtualListInner<T>(
  props: VirtualListProps<T>,
  ref: Ref<VirtualListHandle>
) {
  const {
    data,
    renderItem,
    getItemKey,
    leadingRows = EMPTY_AFFIX_ROWS,
    trailingRows = EMPTY_AFFIX_ROWS,
    estimateSize = 64,
    overscan = 6,
    anchorTo = 'start',
    followOnAppend = false,
    scrollEndThreshold,
    autoScrollToEndOnMount = true,
    fontReadyTimeoutMs = 200,
    maxSettleFrames = 12,
    gap,
    paddingStart,
    paddingEnd,
    reserveEndSpaceForIndex = null,
    onStartReached,
    onEndReached,
    startReachedThreshold = 200,
    endReachedThreshold = 200,
    onAtEndChange,
    gateReachedOnScroll = false,
    onRangeChange,
    className,
    listClassName,
    itemClassName,
    stickyHeader,
    stickyFooter,
    overlay,
    loadingState,
    emptyState,
    isLoading = false,
    role,
    'aria-label': ariaLabel
  } = props;

  const parentRef = useRef<HTMLDivElement>(null);

  /* Row layout is three contiguous bands — [leading affixes][data][trailing
   * affixes] — addressed by one absolute index space. `count` is the TOTAL row
   * count (everything downstream that means "number of rows" already reads
   * `count`, so nothing else changes). `resolveRow` maps an absolute index back
   * to its band; keeping it a plain function (not memoised) is fine — it's only
   * called for the visible window plus the virtualizer's key pass. */
  const leadingCount = leadingRows.length;
  const dataCount = data.length;
  const trailingCount = trailingRows.length;
  const count = leadingCount + dataCount + trailingCount;

  type ResolvedRow =
    | { band: 'affix'; row: AffixRow }
    | { band: 'data'; item: T; dataIndex: number };

  const resolveRow = (index: number): ResolvedRow => {
    if (index < leadingCount) {
      return { band: 'affix', row: leadingRows[index] };
    }
    const dataIndex = index - leadingCount;
    if (dataIndex < dataCount) {
      return { band: 'data', item: data[dataIndex], dataIndex };
    }
    return { band: 'affix', row: trailingRows[dataIndex - dataCount] };
  };

  const estimateFn = useMemo(
    () =>
      typeof estimateSize === 'function' ? estimateSize : () => estimateSize,
    [estimateSize]
  );

  const needsInitialSettle = anchorTo === 'end' && autoScrollToEndOnMount;

  /* External stores for atEnd/settled — both change outside React's render
   * cycle (scroll events, the mount-settle sequence), so both are read via
   * useSyncExternalStore rather than mirrored by hand into useState. */
  const atEndStoreRef = useRef<ReturnType<typeof createBooleanStore>>();
  if (!atEndStoreRef.current) {atEndStoreRef.current = createBooleanStore(true);}
  const settledStoreRef = useRef<ReturnType<typeof createBooleanStore>>();
  if (!settledStoreRef.current) {
    settledStoreRef.current = createBooleanStore(!needsInitialSettle);
  }

  const atEnd = useSyncExternalStore(
    atEndStoreRef.current.subscribe,
    atEndStoreRef.current.getSnapshot
  );
  const settled = useSyncExternalStore(
    settledStoreRef.current.subscribe,
    settledStoreRef.current.getSnapshot
  );

  const startArmedRef = useRef(false);
  const endArmedRef = useRef(false);

  /* Re-arm `onStartReached` when older rows are prepended, so a page shorter
   * than the threshold still triggers the next load. Keep load-older
   * indicators in `stickyHeader`, not `leadingRows`: a leading row breaks the
   * scroll anchor and every page re-fires. */
  const firstDataKey = dataCount > 0 ? getItemKey(data[0], 0) : null;
  useLayoutEffect(() => {
    startArmedRef.current = false;
  }, [firstDataKey]);

  const reserveMinHeightRef = useRef(0);

  const handleChange = useCallback(
    (
      instance: Virtualizer<HTMLDivElement, HTMLDivElement>,
      isScrolling: boolean
    ) => {
      /* While the initial settle sequence (font wait + stabilization loop) is
       * still running, don't commit atEnd or fire start/end-reached — those
       * signals are only meaningful once real row heights (post font-swap,
       * post async-content-resize) are in. The authoritative atEnd value is
       * committed once, atomically with `settled`, when the sequence finishes. */
      if (needsInitialSettle && !settledStoreRef.current!.getSnapshot()) {
        return;
      }

      /* Content can shrink (e.g. a collapsed section) without the real DOM
       * scroll offset being re-clamped by the engine — it only adjusts
       * offset automatically for the anchor/follow-on-append machinery, not
       * on every size change. Left uncorrected, the viewport keeps whatever
       * offset it had, which can now be beyond the shrunk content's height,
       * rendering nothing/a truncated tail instead of the top of what's
       * left. Clamp back to the true max scrollable offset when that
       * happens; `scrollToOffset` re-triggers this callback once more with
       * the corrected offset, which then passes the check below and stops. */
      const maxScrollOffset = Math.max(
        Math.max(instance.getTotalSize(), reserveMinHeightRef.current) -
          (instance.scrollRect?.height ?? 0),
        0
      );
      if ((instance.scrollOffset ?? 0) > maxScrollOffset) {
        dropPendingClampedAdjustment(instance);
        instance.scrollToOffset(maxScrollOffset, { behavior: 'auto' });
      }

      const nowAtEnd = instance.isAtEnd();
      if (nowAtEnd !== atEndStoreRef.current!.getSnapshot()) {
        atEndStoreRef.current!.set(nowAtEnd);
        onAtEndChange?.(nowAtEnd);
      }

      if (onRangeChange) {
        onRangeChange(
          instance.range?.startIndex ?? -1,
          instance.range?.endIndex ?? -1
        );
      }

      /* Opt-in only (see `gateReachedOnScroll` doc) — existing callers that
       * don't set it keep firing on every notification, resize included,
       * exactly as before. */
      if (gateReachedOnScroll && !isScrolling) {
        return;
      }

      const offset = instance.scrollOffset ?? 0;
      const nearStart = offset <= startReachedThreshold;
      if (nearStart) {
        if (!startArmedRef.current) {
          startArmedRef.current = true;
          onStartReached?.();
        }
      } else {
        startArmedRef.current = false;
      }

      const nearEnd = instance.getDistanceFromEnd() <= endReachedThreshold;
      if (nearEnd) {
        if (!endArmedRef.current) {
          endArmedRef.current = true;
          onEndReached?.();
        }
      } else {
        endArmedRef.current = false;
      }
    },
    [
      needsInitialSettle,
      onAtEndChange,
      onRangeChange,
      gateReachedOnScroll,
      onStartReached,
      onEndReached,
      startReachedThreshold,
      endReachedThreshold
    ]
  );

  const virtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
    count,
    getScrollElement: () => parentRef.current,
    estimateSize: estimateFn,
    getItemKey: (index) => {
      const resolved = resolveRow(index);
      return resolved.band === 'data'
        ? getItemKey(resolved.item, resolved.dataIndex)
        : resolved.row.key;
    },
    overscan,
    anchorTo,
    followOnAppend,
    onChange: handleChange,
    ...(scrollEndThreshold != null ? { scrollEndThreshold } : {}),
    ...(gap != null ? { gap } : {}),
    ...(paddingStart != null ? { paddingStart } : {}),
    ...(paddingEnd != null ? { paddingEnd } : {})
  });

  let reserveMinHeight = 0;
  if (reserveEndSpaceForIndex != null && count > 0) {
    const anchorItem =
      virtualizer.measurementsCache[leadingCount + reserveEndSpaceForIndex];
    const viewportHeight = virtualizer.scrollRect?.height ?? 0;
    if (anchorItem && viewportHeight > 0) {
      reserveMinHeight = anchorItem.start + viewportHeight;
    }
  }
  reserveMinHeightRef.current = reserveMinHeight;

  const scrollToIndex = useCallback<VirtualListHandle['scrollToIndex']>(
    (index, opts) =>
      virtualizer.scrollToIndex(leadingCount + index, {
        behavior: 'smooth',
        ...opts
      }),
    [virtualizer, leadingCount]
  );
  const scrollToEnd = useCallback<VirtualListHandle['scrollToEnd']>(
    (opts) => virtualizer.scrollToEnd({ behavior: 'smooth', ...opts }),
    [virtualizer]
  );

  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex,
      scrollToEnd,
      isAtEnd: (threshold?: number) => virtualizer.isAtEnd(threshold),
      measure: () => virtualizer.measure(),
      getVirtualizer: () => virtualizer
    }),
    [virtualizer, scrollToIndex, scrollToEnd]
  );

  /* Initial-mount settle for end-anchored lists. Sequence:
   *  1. (layout phase, pre-paint) land at the estimated end — instant, so
   *     there's never a flash of the top of the thread.
   *  2. Wait for `document.fonts.ready` (capped by `fontReadyTimeoutMs`) —
   *     the font swap is the single biggest source of an all-rows-at-once
   *     height change, so absorb it BEFORE revealing rather than after.
   *  3. Run the stabilization loop: re-land at the end every frame and watch
   *     `getTotalSize()` until it stops changing for two frames running (or
   *     `maxSettleFrames` is hit, as a safety cap).
   *  4. Commit the final atEnd value and flip `settled` — both in the same
   *     tick, so the overlay's first render already reflects the truth. */
  /* The guard is the `settled` STATE, not a one-shot ref. While the list is
   * still unsettled this effect (re)runs the whole sequence and always drives
   * `settled` → true; once settled, every later run early-returns. That single
   * property makes it resilient to the two ways the sequence would otherwise get
   * cancelled-without-restart and leave the list stuck `visibility: hidden`:
   *   1. `count` churn — sending a message bumps `count` twice in quick
   *      succession (Redux appends the optimistic user row, then XState appends
   *      the streaming row one render later); the first bump's cleanup cancels
   *      the in-flight settle, the second run restarts it.
   *   2. Effect double-invoke — React Fast Refresh (on save) and StrictMode run
   *      a cleanup+setup cycle that cancels the settle; because the guard reads
   *      live `settled` (still false) rather than a preserved ref, the re-setup
   *      restarts it instead of early-returning.
   * A one-shot ref (`didInitialScroll`) failed both: the cleanup cancelled the
   * settle while the preserved ref blocked the restart. */
  useLayoutEffect(() => {
    if (settledStoreRef.current!.getSnapshot()) {return;}

    if (!needsInitialSettle) {
      if (count > 0) {settledStoreRef.current!.set(true);}
      return;
    }
    if (count === 0) {return;}

    virtualizer.scrollToEnd({ behavior: 'instant' });

    let cancelStabilize: (() => void) | undefined;
    let cancelled = false;

    waitForFontsReady(fontReadyTimeoutMs).then(() => {
      if (cancelled) {return;}
      cancelStabilize = stabilizeAtEnd(
        virtualizer,
        { maxFrames: maxSettleFrames, stableFramesNeeded: 2 },
        () => {
          if (cancelled) {return;}
          atEndStoreRef.current!.set(virtualizer.isAtEnd());
          settledStoreRef.current!.set(true);
        }
      );
    });

    return () => {
      cancelled = true;
      cancelStabilize?.();
    };
  }, [
    count,
    needsInitialSettle,
    virtualizer,
    fontReadyTimeoutMs,
    maxSettleFrames
  ]);

  const overlayNode =
    typeof overlay === 'function'
      ? overlay({
          isAtEnd: atEnd,
          distanceFromEnd: virtualizer.getDistanceFromEnd(),
          scrollToEnd,
          scrollToIndex
        })
      : overlay;

  const showList = !isLoading && count > 0;
  const virtualItems = showList ? virtualizer.getVirtualItems() : [];

  const scrollContainerStyle = useMemo<CSSProperties>(
    () => ({
      ...SCROLL_CONTAINER_BASE_STYLE,
      // Container still needs real layout/dimensions for the virtualizer to
      // measure against, so hide via visibility, never `display: none`.
      visibility: settled ? 'visible' : 'hidden'
    }),
    [settled]
  );

  return (
    <div className="tw:relative tw:h-full tw:w-full">
      {isLoading ? (
        loadingState ?? null
      ) : count === 0 ? (
        emptyState ?? null
      ) : (
        <div
          ref={parentRef}
          className={className}
          style={scrollContainerStyle}
          role={role}
          aria-label={ariaLabel}
        >
          <div
            className={listClassName}
            style={{
              height: Math.max(virtualizer.getTotalSize(), reserveMinHeight),
              width: '100%',
              position: 'relative'
            }}
          >
            {virtualItems.map((vi) => {
              const resolved = resolveRow(vi.index);
              return (
                <div
                  key={vi.key}
                  data-index={vi.index}
                  ref={virtualizer.measureElement}
                  className={itemClassName}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vi.start}px)`
                  }}
                >
                  {resolved.band === 'data'
                    ? renderItem(resolved.item, resolved.dataIndex)
                    : resolved.row.render()}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showList && settled && stickyHeader ? (
        <div className="tw:absolute tw:inset-x-0 tw:top-0">{stickyHeader}</div>
      ) : null}
      {showList && settled && stickyFooter ? (
        <div className="tw:absolute tw:inset-x-0 tw:bottom-0">
          {stickyFooter}
        </div>
      ) : null}
      {showList && settled ? overlayNode : null}
    </div>
  );
}

/* forwardRef drops the generic, so re-assert it with a cast — the standard
 * generic-forwardRef idiom. Callers get `<VirtualList<Row> ... />` with a typed
 * `ref` of `VirtualListHandle`. */
export const VirtualList = forwardRef(VirtualListInner) as <T>(
  props: VirtualListProps<T> & { ref?: Ref<VirtualListHandle> }
) => ReturnType<typeof VirtualListInner>;

export default VirtualList;
